"use client";

import { c as _c } from "react/compiler-runtime";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Legend } from "recharts";
import { formatGranularDateTick, COLORS } from "./metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import type { ResearchProjectTypeEnumType } from "@research-ai/_database/schema";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";
import { ResearchProjectTypeLabels } from "@research-ai/_database/enums";
import { ResearchProjectTypeValues } from "@research-ai/_database/enums";
const chartConfig: ChartConfig = ResearchProjectTypeValues.reduce((acc, type, index) => {
  acc[type] = {
    label: ResearchProjectTypeLabels[type] || type,
    color: COLORS[index % COLORS.length]
  };
  return acc;
}, {} as ChartConfig);
type ResearchProjectsByTypeProps = {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
};
export const ResearchProjectsByType = props => {
  const $ = _c(21);
  const {
    parentDepartmentId
  } = useDepartmentStackContext();
  let t0;
  if ($[0] !== parentDepartmentId || $[1] !== props.endDate || $[2] !== props.granularity || $[3] !== props.startDate) {
    t0 = {
      startDate: props.startDate,
      endDate: props.endDate,
      granularity: props.granularity,
      parentDepartmentId
    };
    $[0] = parentDepartmentId;
    $[1] = props.endDate;
    $[2] = props.granularity;
    $[3] = props.startDate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const researchMetricsQuery = api.coreMetrics.researchProjectsByType.useQuery(t0);
  let t1;
  if ($[5] !== researchMetricsQuery.data?.chartData) {
    t1 = researchMetricsQuery.data?.chartData.map(_temp) ?? [];
    $[5] = researchMetricsQuery.data?.chartData;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const chartData = t1;
  let t2;
  let typeTotals;
  if ($[7] !== researchMetricsQuery.data?.typeTotals) {
    typeTotals = researchMetricsQuery.data?.typeTotals ?? {} as Record<ResearchProjectTypeEnumType, number>;
    t2 = Object.values(typeTotals).reduce(_temp2, 0);
    $[7] = researchMetricsQuery.data?.typeTotals;
    $[8] = t2;
    $[9] = typeTotals;
  } else {
    t2 = $[8];
    typeTotals = $[9];
  }
  const totalProjects = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="mb-4">{"Research Projects by Type"}</h2>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== chartData.length) {
    t4 = chartData.length === 0 && <div className="text-gray-500">{"No data available for selected range."}</div>;
    $[11] = chartData.length;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== chartData || $[14] !== props.granularity || $[15] !== totalProjects || $[16] !== typeTotals) {
    t5 = chartData.length !== 0 && <><p className="mb-4 text-sm text-muted-foreground">{"Total: "}{totalProjects}{" projects"}{ResearchProjectTypeValues.map(type => <span key={type} className="ml-4">{ResearchProjectTypeLabels[type] || type}:{" "}{typeTotals[type] ?? 0}</span>)}</p><ChartContainer config={chartConfig} className="aspect-auto h-[200px] w-full"><BarChart accessibilityLayer={true} data={chartData}><CartesianGrid vertical={false} /><XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} tickFormatter={value => formatGranularDateTick(value, props.granularity)} /><YAxis orientation="left" /><ChartTooltip content={<ChartTooltipContent nameKey="date" labelFormatter={_temp3} />} /><Legend />{ResearchProjectTypeValues.map(_temp4)}</BarChart></ChartContainer></>;
    $[13] = chartData;
    $[14] = props.granularity;
    $[15] = totalProjects;
    $[16] = typeTotals;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div>{t3}{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
function _temp(item) {
  return {
    date: item.date,
    ...item.typeData
  };
}
function _temp2(acc, val) {
  return acc + val;
}
function _temp3(value_0) {
  return value_0;
}
function _temp4(type_0, index) {
  return <Bar key={type_0} dataKey={type_0} stackId="research" fill={COLORS[index % COLORS.length]} name={ResearchProjectTypeLabels[type_0] || type_0} />;
}